/**
 * Wishlist Page — Web Component
 * Renders saved wishlist items from localStorage into a product grid.
 * Syncs with heart toggles via wishlist:add / wishlist:remove events.
 *
 * localStorage key: ab-wishlist (same as ab-wishlist-heart.js)
 * Data shape per item:
 * { productId, variantId, handle, title, variantTitle, image, price, url, finishHandle, addedAt }
 */
import { ABBaseComponent, defineComponent } from './ab-base-component.js';

class ABWishlistPage extends ABBaseComponent {
  static STORAGE_KEY = 'ab-wishlist';

  connectedCallback() {
    this.grid = this.$('[data-wishlist-grid]');
    this.emptyState = this.$('[data-wishlist-empty]');
    this.toast = this.$('[data-wishlist-toast]');

    // Count badge and share button live outside this element, in the section
    const section = this.closest('.ab-wishlist-page');
    this.countBadge = section?.querySelector('.ab-wishlist-page__count');
    this.shareBtn = section?.querySelector('.ab-wishlist-page__share-btn');

    if (this.shareBtn) {
      this.shareBtn.addEventListener('click', this.handleShare.bind(this));
    }

    // Listen for cross-component wishlist events
    this._onAdd = () => this.render();
    this._onRemove = () => this.render();
    document.addEventListener('wishlist:add', this._onAdd);
    document.addEventListener('wishlist:remove', this._onRemove);

    // Delegate click on remove buttons
    if (this.grid) {
      this.grid.addEventListener('click', (e) => {
        const removeBtn = e.target.closest('[data-wishlist-remove]');
        if (removeBtn) {
          e.preventDefault();
          this.removeItem(removeBtn.dataset.wishlistRemove);
        }
      });
    }

    this.render();
  }

  disconnectedCallback() {
    if (this._onAdd) document.removeEventListener('wishlist:add', this._onAdd);
    if (this._onRemove) document.removeEventListener('wishlist:remove', this._onRemove);
  }

  /**
   * Get all wishlist items from localStorage.
   * @returns {Array}
   */
  getItems() {
    try {
      const raw = localStorage.getItem(ABWishlistPage.STORAGE_KEY);
      if (!raw) return [];
      const items = JSON.parse(raw);
      return Array.isArray(items) ? items : [];
    } catch {
      return [];
    }
  }

  /**
   * Save items back to localStorage.
   * @param {Array} items
   */
  saveItems(items) {
    try {
      localStorage.setItem(ABWishlistPage.STORAGE_KEY, JSON.stringify(items));
    } catch {
      // localStorage unavailable
    }
  }

  /**
   * Remove an item by variantId, update storage, dispatch event, re-render.
   * @param {string} variantId
   */
  removeItem(variantId) {
    const items = this.getItems();
    const filtered = items.filter(item => String(item.variantId) !== String(variantId));
    this.saveItems(filtered);

    // Dispatch event so heart toggles update
    document.dispatchEvent(new CustomEvent('wishlist:remove', {
      bubbles: true,
      detail: { variantId }
    }));

    this.render();
  }

  /**
   * Format price from cents to currency string.
   * @param {string|number} cents
   * @returns {string}
   */
  formatPrice(cents) {
    const amount = parseInt(cents, 10);
    if (isNaN(amount)) return '';
    return `$${(amount / 100).toFixed(2)}`;
  }

  /**
   * Render the full wishlist grid or empty state.
   */
  render() {
    const items = this.getItems();

    // Update count badge
    if (this.countBadge) {
      this.countBadge.textContent = items.length;
      this.countBadge.hidden = items.length === 0;
    }

    // Toggle share button
    if (this.shareBtn) {
      this.shareBtn.hidden = items.length === 0;
    }

    // Toggle empty state vs grid
    if (items.length === 0) {
      if (this.emptyState) this.emptyState.hidden = false;
      if (this.grid) {
        this.grid.hidden = true;
        this.grid.innerHTML = '';
      }
      return;
    }

    if (this.emptyState) this.emptyState.hidden = true;
    if (this.grid) {
      this.grid.hidden = false;
      this.grid.innerHTML = items.map(item => this.renderCard(item)).join('');
    }
  }

  /**
   * Render a single wishlist product card.
   * @param {Object} item
   * @returns {string} HTML string
   */
  renderCard(item) {
    const price = this.formatPrice(item.price);
    const imageUrl = item.image || '';
    const title = this.escapeHtml(item.title || 'Untitled Product');
    const variant = this.escapeHtml(item.variantTitle || '');
    const url = item.url || `/products/${item.handle || ''}`;

    return `
      <div class="ab-wishlist-card">
        <a href="${url}" class="ab-wishlist-card__media">
          ${imageUrl
            ? `<img class="ab-wishlist-card__image" src="${imageUrl}" alt="${title}" loading="lazy" width="400" height="400">`
            : `<div class="ab-wishlist-card__image" style="display:flex;align-items:center;justify-content:center;color:var(--ab-slate-blue);font-size:13px;">No image</div>`
          }
        </a>
        <div class="ab-wishlist-card__info">
          <h3 class="ab-wishlist-card__title">
            <a href="${url}">${title}</a>
          </h3>
          ${variant ? `<p class="ab-wishlist-card__variant">${variant}</p>` : ''}
          ${price ? `<p class="ab-wishlist-card__price">${price}</p>` : ''}
          <div class="ab-wishlist-card__actions">
            <a href="${url}" class="ab-wishlist-card__view-btn">View Product</a>
            <button
              type="button"
              class="ab-wishlist-card__remove-btn"
              data-wishlist-remove="${item.variantId}"
              aria-label="Remove ${title} from wishlist"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Escape HTML to prevent XSS in user-generated content.
   * @param {string} str
   * @returns {string}
   */
  escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  /**
   * Share wishlist: encode variant IDs as URL query params, copy to clipboard.
   */
  async handleShare() {
    const items = this.getItems();
    if (items.length === 0) return;

    const variantIds = items.map(item => item.variantId).join(',');
    const shareUrl = `${window.location.origin}${window.location.pathname}?variants=${encodeURIComponent(variantIds)}`;

    try {
      await navigator.clipboard.writeText(shareUrl);
      this.showToast();
    } catch {
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = shareUrl;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      this.showToast();
    }
  }

  /**
   * Show the clipboard toast notification briefly.
   */
  showToast() {
    if (!this.toast) return;
    this.toast.hidden = false;
    // Force reflow before adding class
    void this.toast.offsetHeight;
    this.toast.classList.add('is-visible');

    clearTimeout(this._toastTimer);
    this._toastTimer = setTimeout(() => {
      this.toast.classList.remove('is-visible');
      setTimeout(() => { this.toast.hidden = true; }, 300);
    }, 2500);
  }
}

defineComponent('ab-wishlist-page', ABWishlistPage);
